import { ingestEvents } from '@/atoms/stats'
import { mutePubkeySetAtom } from '@/atoms/lists'
import { useAtomValue } from 'jotai'
import { repliesMapAtom, addReplies } from '@/atoms/replies'
import { loadMoreTimeline, subscribeTimeline } from '@/services/client.service'
import { TFeedSubRequest } from '@/types'
import * as kinds from '@nostr/tools/kinds'
import { Event, NostrEvent } from '@nostr/tools/wasm'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Tabs from '@/components/Tabs'
import { LoadingBar } from './LoadingBar'
import ReplyNote, { ReplyNoteSkeleton } from './ReplyNote'
import { SubCloser } from '@nostr/tools/abstract-pool'
import { useSecondaryPage } from '@/PageManager'
import { AddressPointer, EventPointer } from '@nostr/tools/nip19'
import { toNote } from '@/lib/link'

const LIMIT = 100
const SHOW_COUNT = 20

export type TExternalContentTab = 'comments' | 'reactions' | 'highlights' | 'bookmarks'

export default function ExternalContentInteractions({
  pageIndex,
  url
}: {
  pageIndex?: number
  url: string
}) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<TExternalContentTab>('comments')

  const tabs = useMemo(
    () => [
      { value: 'comments', label: t('Comments') },
      { value: 'reactions', label: t('Reactions') },
      { value: 'highlights', label: t('Highlights') },
      { value: 'bookmarks', label: t('Bookmarks') }
    ],
    [t]
  )

  return (
    <>
      <Tabs
        value={tab}
        tabs={tabs}
        onTabChange={(v) => setTab(v as TExternalContentTab)}
        threshold={800}
        options={null}
      />
      <div className={tab === 'comments' ? '' : 'hidden'}>
        <CommentsList pageIndex={pageIndex} url={url} />
      </div>
      <div className={tab === 'reactions' ? '' : 'hidden'}>
        <ReactionsList url={url} />
      </div>
      <div className={tab === 'highlights' ? '' : 'hidden'}>
        <HighlightsList url={url} />
      </div>
      <div className={tab === 'bookmarks' ? '' : 'hidden'}>
        <BookmarksList url={url} />
      </div>
    </>
  )
}

function CommentsList({ pageIndex, url }: { pageIndex?: number; url: string }) {
  const { t } = useTranslation()
  const { push, currentIndex } = useSecondaryPage()
  const mutePubkeySet = useAtomValue(mutePubkeySetAtom)
  const repliesMap = useAtomValue(repliesMapAtom)
  const [subRequests, setSubRequests] = useState<null | TFeedSubRequest[]>(null)
  const [until, setUntil] = useState<number | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(false)
  const [showCount, setShowCount] = useState(SHOW_COUNT)
  const [highlightReplyKey, setHighlightReplyKey] = useState<EventPointer | undefined>(undefined)
  const replyRefs = useRef<{ [id: string]: HTMLDivElement | null }>({})
  const bottomRef = useRef<HTMLDivElement | null>(null)

  const replies = useMemo(() => {
    const replyKeySet = new Set<string>()
    const replyEvents: NostrEvent[] = []
    let parentEventKeys = [url]

    while (parentEventKeys.length > 0) {
      const events = parentEventKeys.flatMap((key) => repliesMap.get(key) || [])
      for (const evt of events) {
        if (replyKeySet.has(evt.id)) continue
        if (mutePubkeySet.has(evt.pubkey)) continue
        replyKeySet.add(evt.id)
        replyEvents.push(evt)
      }
      parentEventKeys = events.map((evt) => evt.id)
    }
    return replyEvents.sort((a, b) => a.created_at - b.created_at)
  }, [url, repliesMap, mutePubkeySet])

  useEffect(() => {
    setSubRequests([
      {
        source: 'relays',
        urls: window.hallway.universe.bigRelayUrls,
        filter: {
          '#I': [url],
          kinds: [kinds.Comment, kinds.VoiceComment]
        }
      }
    ])
  }, [url])

  useEffect(() => {
    if (loading || !subRequests || subRequests.length === 0 || currentIndex !== pageIndex) return

    setLoading(true)
    let isClosed = false

    const timeoutId = setTimeout(() => {
      if (!isClosed) {
        setLoading(false)
      }
    }, 10000)

    let subc: SubCloser | undefined
    try {
      subc = subscribeTimeline(
        subRequests,
        {
          limit: LIMIT
        },
        {
          onEvents: (events, isFinal) => {
            if (isFinal) {
              isClosed = true
              clearTimeout(timeoutId)
              setUntil(
                events.length >= LIMIT ? events[events.length - 1].created_at - 1 : undefined
              )
              setLoading(false)
            }
            if (events.length > 0) {
              ingestEvents(events)
              addReplies(events)
            }
          },
          onNew: (evt) => {
            ingestEvents([evt])
            addReplies([evt])
          }
        }
      )
    } catch (_err) {
      clearTimeout(timeoutId)
      setLoading(false)
    }

    return () => {
      clearTimeout(timeoutId)
      subc?.close?.()
    }
  }, [subRequests, currentIndex, pageIndex])

  useEffect(() => {
    if (replies.length === 0) {
      loadMore()
    }
  }, [replies])

  useEffect(() => {
    const observerInstance = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShowCount((prev) => (prev < replies.length ? prev + SHOW_COUNT : prev))
        }
      },
      { root: null, rootMargin: '10px', threshold: 0.1 }
    )
    const currentBottomRef = bottomRef.current
    if (currentBottomRef) observerInstance.observe(currentBottomRef)
    return () => {
      if (observerInstance && currentBottomRef) observerInstance.unobserve(currentBottomRef)
    }
  }, [replies])

  return (
    <div className="min-h-[80vh]">
      {loading && <LoadingBar />}
      {!loading && until && (
        <div
          className={`text-sm text-center text-muted-foreground border-b py-2 ${!loading ? 'hover:text-foreground cursor-pointer' : ''}`}
          onClick={loadMore}
        >
          {t('load more older replies')}
        </div>
      )}
      <div>
        {replies.slice(0, showCount).map((reply) => (
          <div
            ref={(el) => (replyRefs.current[reply.id] = el)}
            key={reply.id}
            className={`scroll-mt-12 ${highlightReplyKey?.id === reply.id ? 'bg-primary/30' : ''}`}
          >
            <ReplyNote
              event={reply}
              onClickParent={(parent: EventPointer | AddressPointer) => {
                if ('identifier' in parent) return
                highlightReply(parent)
              }}
            />
          </div>
        ))}
      </div>
      {!loading && (
        <div className="text-sm mt-2 mb-3 text-center text-muted-foreground">
          {replies.length > 0 ? t('no more replies') : t('no replies')}
        </div>
      )}
      <div ref={bottomRef} />
      {loading && <ReplyNoteSkeleton />}
    </div>
  )

  async function loadMore() {
    if (loading || !until || !subRequests) return

    setLoading(true)
    try {
      const events = await loadMoreTimeline(subRequests, { until, limit: LIMIT })
      addReplies(events)
      setUntil(events.length ? events[events.length - 1].created_at - 1 : undefined)
    } catch (_err) {
      // ignore load-more errors
    } finally {
      setLoading(false)
    }
  }

  function highlightReply(reply: EventPointer, scrollTo = true) {
    let found = false
    if (scrollTo) {
      const ref = replyRefs.current[reply.id]
      if (ref) {
        found = true
        ref.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
    if (!found) {
      if (reply) push(toNote(reply))
      return
    }
    setHighlightReplyKey(reply)
    setTimeout(() => {
      setHighlightReplyKey((pre) => (pre?.id === reply.id ? undefined : pre))
    }, 1500)
  }
}

function ReactionsList({ url }: { url: string }) {
  const { t } = useTranslation()
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [subRequests, setSubRequests] = useState<TFeedSubRequest[]>([])

  useEffect(() => {
    setSubRequests([
      {
        source: 'relays',
        urls: window.hallway.universe.bigRelayUrls,
        filter: {
          '#i': [url],
          kinds: [kinds.ReactionToWebsite],
          limit: 500
        }
      }
    ])
  }, [url])

  useEffect(() => {
    if (subRequests.length === 0) return
    setLoading(true)
    setEvents([])
    const subc = subscribeTimeline(
      subRequests,
      {},
      {
        onEvents: (events, isFinal) => {
          if (isFinal) setLoading(false)
          if (events.length > 0) {
            setEvents(events)
            ingestEvents(events)
          } else if (isFinal) {
            setEvents([])
          }
        },
        onNew: (evt) => {
          ingestEvents([evt])
          setEvents((oldEvents) => {
            if (oldEvents.some((existing) => existing.id === evt.id)) return oldEvents
            return [evt, ...oldEvents].sort((a, b) => b.created_at - a.created_at)
          })
        }
      }
    )
    return () => subc.close()
  }, [subRequests])

  const sorted = useMemo(() => [...events].sort((a, b) => b.created_at - a.created_at), [events])

  if (loading) {
    return (
      <div className="min-h-[40vh] text-sm text-center text-muted-foreground py-6">
        {t('Loading')}
      </div>
    )
  }

  if (sorted.length === 0) {
    return (
      <div className="min-h-[40vh] text-sm text-center text-muted-foreground py-6">
        {t('No reactions yet')}
      </div>
    )
  }

  return (
    <div className="min-h-[80vh] px-4 py-3 flex flex-wrap gap-2">
      {sorted.map((evt) => {
        const content = evt.content || '+'
        return (
          <div key={evt.id} title={evt.content} className="px-2 py-1 border rounded-full text-sm">
            {content}
          </div>
        )
      })}
    </div>
  )
}

function HighlightsList({ url }: { url: string }) {
  const { t } = useTranslation()
  const { push } = useSecondaryPage()
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [subRequests, setSubRequests] = useState<TFeedSubRequest[]>([])

  useEffect(() => {
    setSubRequests([
      {
        source: 'relays',
        urls: window.hallway.universe.bigRelayUrls,
        filter: {
          '#r': [url],
          kinds: [kinds.Highlights],
          limit: 200
        }
      }
    ])
  }, [url])

  useEffect(() => {
    if (subRequests.length === 0) return
    setLoading(true)
    setEvents([])
    const subc = subscribeTimeline(
      subRequests,
      {},
      {
        onEvents: (events, isFinal) => {
          if (isFinal) setLoading(false)
          if (events.length > 0) {
            setEvents(events)
            ingestEvents(events)
          } else if (isFinal) {
            setEvents([])
          }
        },
        onNew: (evt) => {
          ingestEvents([evt])
          setEvents((oldEvents) => {
            if (oldEvents.some((existing) => existing.id === evt.id)) return oldEvents
            return [evt, ...oldEvents].sort((a, b) => b.created_at - a.created_at)
          })
        }
      }
    )
    return () => subc.close()
  }, [subRequests])

  const sorted = useMemo(() => [...events].sort((a, b) => b.created_at - a.created_at), [events])

  if (loading) {
    return (
      <div className="min-h-[40vh] text-sm text-center text-muted-foreground py-6">
        {t('Loading')}
      </div>
    )
  }

  if (sorted.length === 0) {
    return (
      <div className="min-h-[40vh] text-sm text-center text-muted-foreground py-6">
        {t('No highlights yet')}
      </div>
    )
  }

  return (
    <div className="min-h-[80vh] divide-y">
      {sorted.map((evt) => (
        <div
          key={evt.id}
          className="px-4 py-3 cursor-pointer transition-colors hover:bg-muted/30 active:bg-muted/30"
          onClick={() => push(toNote(evt))}
        >
          <div className="flex gap-4">
            <div className="w-1 shrink-0 bg-primary/60 rounded-md" />
            <div className="italic whitespace-pre-line break-words">{evt.content}</div>
          </div>
        </div>
      ))}
    </div>
  )
}

function BookmarksList({ url }: { url: string }) {
  const { t } = useTranslation()
  const { push } = useSecondaryPage()
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [subRequests, setSubRequests] = useState<TFeedSubRequest[]>([])

  const dValue = useMemo(() => url.replace(/^https?:\/\//, ''), [url])

  useEffect(() => {
    setSubRequests([
      {
        source: 'relays',
        urls: window.hallway.universe.bigRelayUrls,
        filter: {
          '#d': [dValue],
          kinds: [kinds.WebBookmarks],
          limit: 200
        }
      }
    ])
  }, [dValue])

  useEffect(() => {
    if (subRequests.length === 0) return
    setLoading(true)
    setEvents([])
    const subc = subscribeTimeline(
      subRequests,
      {},
      {
        onEvents: (events, isFinal) => {
          if (isFinal) setLoading(false)
          if (events.length > 0) {
            setEvents(events)
            ingestEvents(events)
          } else if (isFinal) {
            setEvents([])
          }
        },
        onNew: (evt) => {
          ingestEvents([evt])
          setEvents((oldEvents) => {
            if (oldEvents.some((existing) => existing.id === evt.id)) return oldEvents
            return [evt, ...oldEvents].sort((a, b) => b.created_at - a.created_at)
          })
        }
      }
    )
    return () => subc.close()
  }, [subRequests])

  const sorted = useMemo(() => [...events].sort((a, b) => b.created_at - a.created_at), [events])

  if (loading) {
    return (
      <div className="min-h-[40vh] text-sm text-center text-muted-foreground py-6">
        {t('Loading')}
      </div>
    )
  }

  if (sorted.length === 0) {
    return (
      <div className="min-h-[40vh] text-sm text-center text-muted-foreground py-6">
        {t('No bookmarks yet')}
      </div>
    )
  }

  return (
    <div className="min-h-[80vh] divide-y">
      {sorted.map((evt) => {
        const title = evt.tags.find((tag) => tag[0] === 'title')?.[1]
        return (
          <div
            key={evt.id}
            className="px-4 py-3 cursor-pointer transition-colors hover:bg-muted/30 active:bg-muted/30"
            onClick={() => push(toNote(evt))}
          >
            <div className="font-semibold break-words">{title || evt.content || dValue}</div>
            {title && evt.content && (
              <div className="text-sm text-muted-foreground line-clamp-3 mt-1 break-words whitespace-pre-wrap">
                {evt.content}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
